import { Option } from "@/components/HierarchicalMultiSelect";
import { SelectOptionType } from "@/partial/template/types";
import { create } from "zustand";

interface CampaignState {
  campaignDetail: any | null;
  optionsDataFile: SelectOptionType[] | null;
  optionsConditionChannel: SelectOptionType[] | null;
  optionsAdditionalParameters: SelectOptionType[] | [];
  customerParametersOptions: {
    optionsState: SelectOptionType[];
    optionsCity: SelectOptionType[];
    optionsArea: SelectOptionType[];
    optionsPincode: SelectOptionType[];
  } | null;
  loanParametersOptions: {
    optionsPortfolioCategory: Option[];
    optionsDealerBranch: SelectOptionType[];
  } | null;
  channelParametersOptions:
    | {
        campaignDataChannels: {
          campaignTemplate: SelectOptionType;
        }[];
      }[]
    | null;
  initializeCampaignDetail: (campaignDetail: any) => void;
  setOptionsConditionChannel: (optionsCondition: SelectOptionType[]) => void;
  setOptionsAdditionalParameters: any;
}

const getInitialOptionsDataFile = (campaignDetail: any): any[] => {
  try {
    return campaignDetail?.uploadFile
      ? [
          {
            value: campaignDetail?.uploadFile?.id,
            label: campaignDetail?.uploadFile?.fileName,
            other: {
              excelHeaders: campaignDetail?.uploadFile?.excelHeaders,
              headerMapping: campaignDetail?.uploadFile?.headerMapping,
              user_field_mapping: campaignDetail?.uploadFile?.userFieldMapping,
            },
          },
        ]
      : [];
  } catch (e) {
    console.log("Something went wrong:", e);
    return [];
  }
};

const getInitialCustomerParametersOptions = (campaignDetail: any): any => {
  try {
    if (!campaignDetail?.customerParams) return null;
    const { states, districts, areas, pincodes } =
      campaignDetail.customerParams;
    return {
      optionsState: states.map((state: any) => ({
        value: state.id,
        label: state.name,
      })),
      optionsCity: districts.map((district: any) => ({
        value: district.id,
        label: district.name,
      })),
      optionsArea: areas.map((area: any) => ({
        value: area.id,
        label: area.name,
      })),
      optionsPincode: pincodes.map((pincode: any) => ({
        value: pincode.id,
        label: pincode.pincode,
      })),
    };
  } catch (e) {
    console.log("Something went wrong:", e);
    return null;
  }
};

const getInitialLoanParametersOptions = (campaignDetail: any): any => {
  try {
    if (!campaignDetail?.loanParams) return null;
    const { portfolioCategory, dealerBranch } = campaignDetail.loanParams;
    return {
      optionsPortfolioCategory: portfolioCategory.map((category: any) => ({
        value: category.id,
        label: category.value,
        name: category.name,
      })),
      optionsDealerBranch: dealerBranch.map((branch: string) => ({
        value: branch,
        label: branch,
      })),
    };
  } catch (e) {
    console.log("Something went wrong:", e);
    return null;
  }
};

const getInitialChannelParametersOptions = (campaignDetail: any): any => {
  try {
    if (!campaignDetail?.campaignChannels) return null;
    const { campaignChannels } = campaignDetail;
    return campaignChannels.map((channel: any) => {
      return {
        campaignDataChannels: channel.campaignDataChannels.map(
          (dataChannel: any) => {
            return {
              campaignTemplate: {
                value: dataChannel.campaignTemplate.id,
                label: dataChannel.campaignTemplate.name,
              },
            };
          }
        ),
      };
    });
  } catch (e) {
    console.log("Something went wrong:", e);
    return null;
  }
};

const useCampaignStore = create<CampaignState>((set) => ({
  campaignDetail: null,
  optionsDataFile: null,
  optionsConditionChannel: null,
  optionsAdditionalParameters: [],
  customerParametersOptions: null,
  loanParametersOptions: null,
  channelParametersOptions: null,
  initializeCampaignDetail: (campaignDetail: any) => {
    set({
      campaignDetail,
      optionsDataFile: getInitialOptionsDataFile(campaignDetail),
      customerParametersOptions:
        getInitialCustomerParametersOptions(campaignDetail),
      loanParametersOptions: getInitialLoanParametersOptions(campaignDetail),
      channelParametersOptions:
        getInitialChannelParametersOptions(campaignDetail),
    });
  },
  setOptionsConditionChannel: (optionsCondition: SelectOptionType[]) => {
    set({
      optionsConditionChannel: optionsCondition,
    });
  },
  setOptionsAdditionalParameters: (
    optionsAdditionalParams: SelectOptionType[]
  ) => {
    set({
      optionsAdditionalParameters: optionsAdditionalParams,
    });
  },
}));

export default useCampaignStore;
